import { CalendarPlus, MessageCircle, Package, PawPrint, Phone } from 'lucide-react'
import { useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { HEALTHY_SUBSCRIPTION_STATUSES, SNOOTS_HQ, startOfDayUtc } from '@snootsvet/shared/portal'

import { ActionCard } from '@/customer/components/action-card'
import { BlockedActionDialog } from '@/customer/components/blocked-action-dialog'
import { ManageAppointmentDialog } from '@/customer/components/booking/manage-appointment-dialog'
import { RoutineAppointmentFlow } from '@/customer/components/booking/routine-appointment-flow'
import { UpcomingVisitCard } from '@/customer/components/booking/upcoming-visit-card'
import { LoadingSpinner } from '@/customer/components/loading-spinner'
import { MembershipWarnings } from '@/customer/components/membership-warnings'
import { NextStepsChecklist } from '@/customer/components/next-steps-checklist'
import { ReadyOrdersCard } from '@/customer/components/orders/ready-orders-card'
import { PageContainer } from '@/customer/components/page-container'
import { Card, CardContent, CardHeader } from '@/customer/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/customer/components/ui/dialog'
import { USContactDetails, USContactLinks } from '@/customer/components/us-contact-details'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetMe } from '@/customer/hooks/use-get-me'
import { useListAppointments } from '@/customer/hooks/use-list-appointments'
import { useListOrders } from '@/customer/hooks/use-list-orders'
import { useMembershipIssues } from '@/customer/hooks/use-membership-issues'
import { useOnboardingIssues } from '@/customer/hooks/use-onboarding-issues'
import {
  type PetWithSubscription,
  usePetsWithSubscriptions,
} from '@/customer/hooks/use-pets-with-subscriptions'
import { useRegion } from '@/customer/hooks/use-region'
import { useRegionClinics } from '@/customer/hooks/use-region-clinics'
import { routes } from '@/customer/routes'

/**
 * Portal home.
 *
 * MVP scope: greet the member, surface their next visit when one
 * exists, and expose a small set of primary actions - booking, and
 * two WhatsApp escape hatches for anything else. A "coming soon"
 * card anchors the page while most of the portal is still stubbed,
 * and doubles as the empty state when there's no upcoming visit.
 *
 * Booking gating: `Book a visit` opens the booking flow when the
 * member is eligible, or a dialog with the remaining requirements.
 */
export function AccountPage() {
  useDocumentTitle('Your account')
  const navigate = useNavigate()
  const me = useGetMe()
  const region = useRegion()
  const petsWithSubs = usePetsWithSubscriptions()
  const appointments = useListAppointments()

  // Controls the "Book a routine visit" dialog. Kept as local state
  // rather than a route param so the dialog can close without a
  // navigation and doesn't pollute the browser history.
  const [bookOpen, setBookOpen] = useState(false)
  const [bookingBlockedOpen, setBookingBlockedOpen] = useState(false)

  // Controls the manage-appointment dialog. Opened from
  // `UpcomingVisitCard`; same rationale as above for keeping it in
  // local state rather than the URL.
  const [manageOpen, setManageOpen] = useState(false)
  const [contactOpen, setContactOpen] = useState(false)

  const firstName = me.data?.member.firstName ?? null

  const onboarding = useOnboardingIssues()
  const membership = useMembershipIssues()
  const readyOrders = useListOrders({ status: ['prepared'] })
  const headline = [
    { text: firstName ? `Hi ${firstName},` : 'Hi there,', color: 'yellow' as const },
    { text: 'welcome back', color: 'blue' as const },
  ]

  // Wait on `/me` before rendering the page shell - almost every
  // downstream decision reads from it, and a premature render would
  // just flash the disabled/enabled states as data lands.
  if (me.isPending || !me.data) {
    return (
      <PageContainer headline={headline}>
        <LoadingSpinner />
      </PageContainer>
    )
  }

  const { patients } = me.data
  const today = startOfDayUtc(region.timezone)
  const nextAppointment =
    appointments.data?.appointments.find(
      (a) => a.status !== 'cancelled' && new Date(a.startTime) >= today
    ) ?? null
  const nextPet = nextAppointment
    ? (patients.find((p) => p.id === nextAppointment.patientId) ?? null)
    : null
  const showNotificationsSection =
    membership.issues.length > 0 ||
    onboarding.issues.length > 0 ||
    (readyOrders.data?.orders.length ?? 0) > 0 ||
    (nextAppointment !== null && nextPet !== null)

  const bookingChecksPending = onboarding.isPending || membership.isPending
  const bookingBlockers = [...onboarding.issues, ...membership.issues]
  const canBookAnything = bookingBlockers.length === 0

  function openBooking() {
    if (canBookAnything) setBookOpen(true)
    else setBookingBlockedOpen(true)
  }

  return (
    <PageContainer headline={headline}>
      <div className="flex flex-col gap-8 md:gap-12">
        {showNotificationsSection ? (
          <section className="flex flex-col gap-6">
            {membership.issues.length > 0 ? (
              <MembershipWarnings issues={membership.issues} />
            ) : null}
            {onboarding.issues.length > 0 ? (
              <NextStepsChecklist issues={onboarding.issues} />
            ) : null}

            {readyOrders.data && readyOrders.data.orders.length > 0 ? (
              <ReadyOrdersCard orders={readyOrders.data.orders} />
            ) : null}

            {nextAppointment && nextPet ? (
              <UpcomingVisitCard
                appointment={nextAppointment}
                pet={nextPet}
                region={region}
                onOpen={() => setManageOpen(true)}
              />
            ) : null}
          </section>
        ) : null}
        <section className="flex flex-col gap-3">
          <h2 className="text-button text-muted-foreground">What can we help with?</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <ActionCard
              icon={CalendarPlus}
              label="Book a visit"
              tone="pink"
              disabled={bookingChecksPending}
              onClick={openBooking}
            />
            {region.id === 'uk' ? (
              <ActionCard
                icon={MessageCircle}
                label="Message us"
                tone="yellow"
                href={SNOOTS_HQ.whatsappUrl}
              />
            ) : (
              <ActionCard
                icon={Phone}
                label="Contact us"
                tone="yellow"
                onClick={() => setContactOpen(true)}
              />
            )}
            <ActionCard
              icon={Package}
              label="View your orders"
              tone="green"
              onClick={() => navigate(routes.account.orders)}
            />
          </div>
        </section>

        <Card color="baby-blue">
          <CardHeader>
            <h3 className="text-body font-bold">We're working on a new Snoots member experience</h3>
          </CardHeader>
          <CardContent className="gap-y-3">
            <p>We'll be rolling out more functionality over the coming weeks.</p>
            {region.id === 'uk' ? (
              <p>
                In the meantime, WhatsApp us at{' '}
                <a
                  href={SNOOTS_HQ.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold underline underline-offset-4"
                >
                  {SNOOTS_HQ.phone}
                </a>{' '}
                for anything else you need.
              </p>
            ) : (
              <>
                <p>Get in touch for anything else you need:</p>
                <USContactLinks />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <BookVisitDialog open={bookOpen} onOpenChange={setBookOpen} pets={petsWithSubs.pets} />
      <BlockedActionDialog
        open={bookingBlockedOpen}
        onOpenChange={setBookingBlockedOpen}
        blockers={bookingBlockers}
      />

      {/*
       * The manage dialog is only mounted when an upcoming visit is
       * actually present - `nextAppointment` also gates the
       * `UpcomingVisitCard`, so if there's nothing to manage, there's
       * nothing to render.
       */}
      {nextAppointment && nextPet ? (
        <ManageAppointmentDialog
          open={manageOpen}
          onOpenChange={setManageOpen}
          appointment={nextAppointment}
          pet={nextPet}
          region={region}
        />
      ) : null}

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent>
          <DialogHeader tone="yellow">
            <Phone className="size-6" />
            <DialogTitle>Contact us</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <USContactDetails />
          </DialogBody>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}

/**
 * Portal booking-flow dialog. Wraps `RoutineAppointmentFlow` in the
 * branded modal chrome and picks the bookable-pet subset from the
 * fanned-out subscription state.
 *
 * The pet picker is force-shown when the member has non-bookable
 * pets alongside a single bookable one - the single-option picker
 * cues them that another of their pets is missing from the list
 * (usually because it has no active subscription).
 */
function BookVisitDialog({
  open,
  onOpenChange,
  pets,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pets: PetWithSubscription[]
}) {
  const navigate = useNavigate()
  const bookablePets = pets.filter(
    (pet) =>
      pet.subscriptionQuery.data?.subscription &&
      HEALTHY_SUBSCRIPTION_STATUSES.has(pet.subscriptionQuery.data.subscription.status)
  )
  const alwaysShowPetPicker = pets.length > 1 && bookablePets.length === 1
  const clinics = useRegionClinics()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader tone="yellow">
          <PawPrint className="size-6" />
          <DialogTitle>Book a visit</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <RoutineAppointmentFlow
            pets={bookablePets}
            clinics={clinics}
            alwaysShowPetPicker={alwaysShowPetPicker}
            onProceedToBooking={(values) => {
              onOpenChange(false)
              const path = generatePath(routes.account.pets.book, {
                patientId: values.patientId,
              })
              const query = new URLSearchParams({
                type: values.appointmentTypeId,
                clinic: values.clinicId,
              })
              navigate(`${path}?${query}`)
            }}
          />
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
